<template>
  <div class="mb-16 flex flex-col items-center">
    <ClientOnly>
      <RevCarousel
        :alternative-button-label="i18n(translations.alternativeController)"
        :alternative-next="i18n(translations.alternativeNext)"
        :alternative-previous="i18n(translations.alternativePrevious)"
        class="w-full"
        current-index-id="condition-guidance"
        @slide-impression="onSlideImpression"
      >
        <div
          v-for="image in images"
          :key="image.src"
          class="rounded-lg relative flex w-full"
        >
          <RevIllustration
            :alt="i18n(image.alt)"
            class="rounded-lg hidden h-auto w-full md:block"
            :height="873"
            :src="image.src + '_md.jpg'"
            :width="1164"
            @load="onImageLoaded"
          />
          <RevIllustration
            :alt="i18n(image.alt)"
            class="rounded-lg h-auto w-full md:hidden"
            :height="600"
            :src="image.src + '_sm.jpg'"
            :width="600"
            @load="onImageLoaded"
          />

          <p class="absolute inset-x-0 bottom-0 mx-24 mb-8 flex justify-center">
            <span
              class="bg-static-default-low rounded-sm caption p-4 text-center"
            >
              {{ i18n(image.alt) }}
            </span>
          </p>

          <div
            v-if="shouldDisplayLoader"
            class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            <RevSpinner size="large" />
          </div>
        </div>
      </RevCarousel>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevCarousel } from '@ds/components/Carousel'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSpinner } from '@ds/components/Spinner'

import { useUrlParams } from '../../../../composables/useUrlParams'

import { GRADE_TO_GALLERY } from './ConditionGuidance.constants'
import translations from './ConditionGuidance.translations'

const props = withDefaults(
  defineProps<{
    trackingModel?: string
  }>(),
  {
    trackingModel: '',
  },
)

const { trackProductBlockImpression } = useTracking()
const { grade } = useUrlParams()
const i18n = useI18n()

const displayedIndex = ref(0)
const loaded = ref<Record<number, Record<number, boolean>>>({
  10: { 0: false, 1: false, 2: false },
  11: { 0: false, 1: false, 2: false },
  12: { 0: false, 1: false, 2: false },
})

const images = computed(() => {
  if (!grade.value) {
    return []
  }

  return GRADE_TO_GALLERY[grade.value]
})

const shouldDisplayLoader = computed(() => {
  if (!grade.value) {
    return false
  }

  return !loaded.value[grade.value][displayedIndex.value]
})

function onSlideImpression({ index }: { index: number }) {
  // This event is triggered a bit to much (at each rerender I guess), so let's have a small
  // condition to prevent sending duplicates
  if (index !== displayedIndex.value) {
    trackProductBlockImpression({
      block_name: 'grade_image',
      position: index + 1,
      product_model: props.trackingModel ?? '',
    })
    displayedIndex.value = index
  }
}

function onImageLoaded() {
  if (grade.value) {
    loaded.value[grade.value][displayedIndex.value] = true
  }
}
</script>
